import { useParams } from "react-router-dom";
import {
  useFetchOrganizationLogos,
  useSetMainLogo,
  useDeleteOrganizationMedia,
} from "@core/features/organization";
import { Loader } from "@themes/pollinator/ui/Loader";
import { LogosTable } from "./LogosTable";

export type Props = {
  /** Optional organization ID override (defaults to URL param) */
  organizationId?: number;
};

/**
 * Container component for LogosTable.
 * Handles data fetching and mutations for organization logos.
 *
 * Note: Authorization is handled at the page level (OrganizationAdminPage).
 * This container trusts that it's only rendered for authorized users.
 */
export function LogosTableContainer({ organizationId: organizationIdProp }: Props) {
  const params = useParams<{ organizationId: string }>();
  const organizationId = organizationIdProp ?? Number(params.organizationId);

  const { data: logos, isLoading } = useFetchOrganizationLogos({
    organizationId,
    enabled: !!organizationId,
  });

  const setMainLogoMutation = useSetMainLogo();
  const deleteMutation = useDeleteOrganizationMedia();

  const handleSetMainLogo = (mediaId: number) => {
    setMainLogoMutation.mutate({ organizationId, mediaId });
  };

  const handleDelete = (mediaId: number) => {
    deleteMutation.mutate({ organizationId, mediaId });
  };

  if (isLoading || !logos) {
    return <Loader isFullPage />;
  }

  return (
    <LogosTable
      logos={logos}
      onSetMainLogo={handleSetMainLogo}
      onDelete={handleDelete}
      isSettingMainLogo={setMainLogoMutation.isPending}
      isDeleting={deleteMutation.isPending}
    />
  );
}

export default LogosTableContainer;

